<template>
    <b-container id="question-for-print" fluid class="py-2 px-0 position-relative">

        <!-- Question & Question info 영역 -->
        <h4 class="fw-600 px-7 pb-2 border-bottom border-color-gray-300">
            {{ showTranslation(question_name, question_name_e, question_name_c) }}
        </h4>

        <!-- Sub-component 영역 -->
        <div v-if="subs_loaded && subs.length > 0" class="px-7 pb-5 mb-4">
            <sub-component v-for="(item, index) in subs"
                :key="`print-questions-${question_code}-sub-${item.sub_code}`" 
                :sub_type="item.sub_type"
                :item="item"
                :is_disabled_by_parent="false"
                :print_mode="true"
                :is_final_sub_component="index == subs.length - 1"
                @update-pipeline="handleUpdatePipeline"
                @load-finished="handleLoadFinished"
                @sub-load-finished="handleSubLoadFinished"
                @update-sub-for-print="handleUpdateSubForPrint"
                >
            </sub-component>
        </div>
    </b-container>
</template>

<script>
import { mapState } from 'vuex';
import SubComponent from '@core_components/client/components/subs/SubComponent.vue';
import { generateSubcomponentDataBy } from '@core_utils/subComponent/subComponent';
import * as th from 'tree-helper';

// client SubComponent.vue 가 그리는 유형. 그 밖의 유형(폐기)은 그려지지 않아 완료 신호가 오지 않으므로 세지 않는다
const RENDERED_SUB_TYPES = ['checkbox', 'table', 'radio', 'file_URL2', 'input_text', 'input_number', 'textbox', 'materiality', 'priority', 'guide_text', 'guide_text2'];

export default {
    name: 'QuestionForPrint',
    props: {
        question_code: {
            type: String,
            default: ''
        },
        is_final_question_code: {
            type: Boolean,
            default: false
        }
    },
    components: {
        SubComponent
    },
    data () {
        return {
            question_name: '',
            question_name_e: '',
            question_name_c: '',
            question_infobox: '',
            template_name: '',
            is_writable: true,
            is_visible: true,
            is_completed: false,
            comment: '',
            pipeline_key: 100,
            subs: [],
            subs_loaded: false,
            load_complete_sub: [],
            question_contents: [],

            load_finished: false
        };
    },
    computed: {
        ...mapState(['template_in_progress', 'template_tree', 'view_mode']),
        ...mapState('questions', ['answer_company']),
        // 인쇄 버튼이 answer_company 로 문항 트리를 만들므로 query 가 없으면 같은 대상으로 답변을 읽는다
        target_campaign_id () {
            return this.$route.query.cmp || this.$route.query.campaign_id || this.answer_company.campaign_id;
        },
        target_company_id () {
            return this.$route.query.cpn || this.$route.query.company_id || this.answer_company.company_id;
        },
        target_template_contents () {
            if (this.view_mode=='provider') {
                return this.template_in_progress.template_contents;
            } else {
                return this.template_tree.template_contents;
            }
        }
    },
    async created () {
        await this.initializeQuestion(this.question_code);
    },
    beforeDestroy () {
        this.question_name = '';
        this.question_name_e = '';
        this.question_name_c = '';
        this.question_infobox = '';
        this.template_name = '';
        this.is_writable = true;
        this.is_visible = true;
        this.is_completed = false;
        this.comment = '';
        this.pipeline_key = 100;
        this.subs = [];
        this.subs_loaded = false;
        this.load_complete_sub = [];
        this.question_contents = [];
        this.load_finished = false;
    },
    methods: {
        async initializeQuestion (question_code) {
            th.breadthFirstSearch(this.target_template_contents, async (node) => {
                if (node.question_code === question_code) {
                    this.comment = node.comment;
                    this.question_name = node.question_name;
                    this.question_name_e = node.question_name_e;
                    this.question_name_c = node.question_name_c;
                    this.question_infobox = node.question_infobox;
                    this.is_writable = node.is_writable;
                    this.is_visible = node.is_visible;
                    
                    if (typeof node.question_contents === 'string') {
                        this.question_contents = JSON.parse(node.question_contents);
                    } else {
                        this.question_contents = node.question_contents;
                    }
                    
                    this.is_completed = node.is_completed;
                }
            });

            if (!this.question_contents?.length) {
                this.handleLoadFinished();
                return;
            }
            await this.readSubs(this.question_contents);
        },
        async readSubs (question_contents) {
            this.subs = [];
            this.subs_loaded = false;

            try {
                let _subs = [];

                const _question_contents = Array.isArray(question_contents) ? question_contents.join(',') : question_contents;
                const _res_subs = await this.$axios.get('/subs', {
                    params: {
                        company_id: this.target_company_id,
                        campaign_id: this.target_campaign_id,
                        sub_codes: _question_contents
                    }
                }
                );

                if (_res_subs.data && _res_subs.data.length) {
                    _res_subs.data.forEach(_sub => {
                        if (RENDERED_SUB_TYPES.includes(_sub.sub_type)) {
                            _subs.push({...generateSubcomponentDataBy(_sub), question_code: this.question_code});
                        }
                    });
                }

                this.subs = _subs;
                this.$nextTick(() => {
                    this.subs_loaded = true;
                });
                // 그릴 sub 가 없으면 완료 이벤트가 오지 않는다
                if (!_subs.length) {
                    this.handleLoadFinished();
                }
            } catch (error) {
                if (error?.response?.status === 401) return;
                console.error(error);
                this.$emit('load-error', this.question_code);
            }
        },
        handleUpdateSubForPrint (updated_sub) {
            const idx = this.subs.findIndex(item => item.sub_code === updated_sub.sub_code);
            if (idx > -1) {
                this.$set(this.subs, idx, generateSubcomponentDataBy({ ...this.subs[idx], ...updated_sub }));
            }
        },
        handleSubLoadFinished (loaded_sub_code) {
            this.load_complete_sub.push(loaded_sub_code);
            
            if (this.load_complete_sub.length === this.subs.length) {
                this.handleLoadFinished();
            }
        },
        // 인쇄 버튼이 완료 수로 진행률·동시 요청 수를 세므로 문항당 한 번만 알린다
        handleLoadFinished(){
            if (this.load_finished) return;
            this.load_finished = true;
            this.$emit('load-finished', this.question_code);
        },
        handleUpdatePipeline(){
            this.pipeline_key += 1;
        }
    }
};
</script>
